/**
 * Brief Form Page
 * Multi-step form with questions based on brief type and business type.
 * All display text is resolved from i18n translation files via useTranslations('brief').
 *
 * Question branching:
 * - Detailed (B2B + B2C) → shared detailed questions (8 pages)
 * - Short + B2B → B2B-specific short questions
 * - Short + B2C → B2C-specific short questions
 */
import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowLeft, ArrowRight, Check, Upload, X, Search, Loader2, Plus, Minus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/select'
import { CheckboxMark } from '@/components/ui/checkbox'
import { fieldErrorClassName } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import { useTranslations } from '@/hooks/use-translations'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/use-auth'
import { useActiveTeam } from '@/hooks/use-team'
import TomSelect from 'tom-select'
import 'tom-select/dist/css/tom-select.css'
import { api } from '@/lib/api'
import { toast } from 'sonner'

// ─── Field Types ────────────────────────────────────────────────────────────

interface TextField {
    id: string
    label: string
    type: 'text'
    placeholder?: string
    required?: boolean
    width?: 'full' | 'half'
    hint?: string
    labelStyle?: 'h2'
}

interface TextareaField {
    id: string
    label: string
    type: 'textarea'
    placeholder?: string
    required?: boolean
    hint?: string
    labelStyle?: 'h2'
}

interface DropdownField {
    id: string
    label: string
    type: 'dropdown'
    options: string[]
    values?: string[]
    required?: boolean
    width?: 'full' | 'half'
}

interface CheckboxGroupField {
    id: string
    label: string
    type: 'checkbox-group'
    options: string[]
    columns?: number
    hint?: string
    labelStyle?: 'h2'
    required?: boolean
}

interface MultiSelectField {
    id: string
    label: string
    type: 'multi-select'
    options: string[]
}

interface SliderField {
    id: string
    label: string
    type: 'slider'
    min: number
    max: number
    defaultValue: number
    unit: string
    prefix?: string
    formatValue?: (val: number) => string
    required?: boolean
}

interface FileUploadField {
    id: string
    label: string
    type: 'file-upload'
    accept: string
    hint?: string
    labelStyle?: 'h2'
}

interface SocialInputsField {
    id: string
    label: string
    type: 'social-inputs'
    networks: { key: string; name: string; placeholder: string }[]
}

interface RadioGroupField {
    id: string
    label: string
    type: 'radio-group'
    options: string[]
    hint?: string
    labelStyle?: 'h2'
    required?: boolean
}

interface DateField {
    id: string
    label: string
    type: 'date'
    width?: 'full' | 'half'
    hint?: string
    labelStyle?: 'h2'
}

type BriefField = TextField | TextareaField | DropdownField | CheckboxGroupField | MultiSelectField | SliderField | FileUploadField | SocialInputsField | RadioGroupField | DateField

interface Step {
    title: string
    description: string
    fields: BriefField[]
}

// ─── Option key lists: structure → translation lookup ───────────────────────

const SOCIAL_NETWORK_KEYS = ['instagram', 'telegram', 'facebook', 'tiktok', 'youtube', 'linkedin'] as const
const MAIN_GOAL_KEYS = ['selectMainGoal', 'attractLeads', 'brandAwareness', 'launchProduct', 'increaseSales', 'driveTraffic', 'increaseEngagement', 'appInstalls'] as const
const EXPECTED_ACTION_KEYS = ['applyForm', 'goToSite', 'registration', 'subscription', 'officeVisit', 'phoneCall', 'downloadFile', 'buy', 'appInstall', 'event'] as const
const GENDER_KEYS = ['select', 'male', 'female', 'any'] as const
const INCOME_KEYS = ['select', 'low', 'medium', 'high', 'premium'] as const
const INTEREST_KEYS = [
    'fitnessGym', 'yogaPilates', 'running', 'swimming', 'cycling', 'teamSports', 'hiking', 'skiing',
    'martialArts', 'dancing', 'drawing', 'photography', 'music', 'cooking', 'gardening', 'handicrafts',
    'collecting', 'boardGames', 'puzzles', 'modeling', 'exoticTravel', 'beachVacation', 'cityTourism',
    'historicalTours', 'culturalEvents', 'ecotourism', 'roadTrips', 'languages', 'cruises', 'spiritualRetreats',
    'reading', 'onlineCourses', 'psychology', 'publicSpeaking', 'professionalSkills', 'financialLiteracy',
    'history', 'science', 'foreignLanguages', 'leadership', 'gadgets', 'programming', 'ai', 'vrAr',
    'robotics', 'crypto', 'esports', 'cars', 'smartHomes', 'space', 'movies', 'theater', 'paintingSculpture',
    'fashion', 'literature', 'festivals', 'comics', 'anime', 'podcasts', 'standUp', 'parenting', 'pets',
    'interiorDecor', 'renovation', 'familyCooking', 'spaceOrg', 'gardenGrowing', 'homeHolidays', 'homeSafety',
    'investments', 'realEstate', 'entrepreneurship', 'marketingAds', 'personalBrand', 'sales', 'ecommerce',
    'financialPlanning', 'careerGrowth', 'businessTravel', 'healthyEating', 'meditation', 'healthyLifestyle',
    'massage', 'mentalHealth', 'altMedicine', 'vitamins', 'spa', 'weightLoss', 'sportsNutrition',
] as const
const ONLINE_KEYS = ['telegramChannels', 'instagram', 'tiktok', 'youtube', 'facebook', 'linkedin', 'olx', 'yandex', 'google', 'forums', 'blogs', 'news'] as const
const OFFLINE_KEYS = ['shoppingMalls', 'officeCenters', 'subway', 'busStops', 'gyms', 'cafes', 'universities', 'medicalCenters', 'banks', 'carDealerships', 'beautySalons', 'conferences'] as const
const DIGITAL_ADS_KEYS = ['telegramAds', 'googleAds', 'linkedinAds', 'youtubeAds', 'emailMarketing', 'telegramChannels', 'metaAds', 'yandexDirect', 'tiktokAds', 'smsMailing', 'influenceMarketing', 'contextualAds', 'seo', 'programmatic', 'affiliate', 'arVr'] as const
const OOH_KEYS = ['ledScreens', 'cityFormat', 'busStopBranding', 'mallAds', 'billboards', 'subwayAds', 'transportAds', 'elevatorAds', 'dooh'] as const
const TRADITIONAL_KEYS = ['tv', 'print', 'events', 'radio', 'pr'] as const
const OFFLINE_ACT_KEYS = ['btl', 'eventsActivations', 'gamification', 'partnerships', 'ambassadorUgc'] as const
const EXPECTED_TASKS_KEYS = ['strategy', 'adCampaigns', 'analytics', 'optimization', 'emailMarketing', 'pr', 'creatives', 'socialMedia', 'abTesting', 'landingPage', 'bloggers', 'techSupport'] as const
const WORK_PROCESS_KEYS = ['speed', 'creative', 'reporting', 'proactivity', 'transparency', 'techExpertise', 'flexibility', 'deadlines'] as const

// Backend enum values for dropdown fields (must align index-by-index with display option keys above)
const MAIN_GOAL_VALUES = ['', 'attract_applications', 'increase_brand_awareness', 'launch_new_product', 'increase_sales', 'attract_traffic_site', 'increase_engagement', 'increase_app_install'] as const
const GENDER_VALUES = ['', 'male', 'female', 'any'] as const
const INCOME_VALUES = ['', 'low', 'medium', 'high', 'premium'] as const

// Short B2B option keys
const B2B_SUCCESS_KEYS = ['roas', 'costPerSql', 'cac', 'ltvCac', 'pipelineValue', 'otherKpi'] as const

// Short B2C option keys
const B2C_OBJECTIVE_KEYS = ['increaseSales', 'sellOutProduct', 'reduceCPA', 'brandAwareness', 'acquireUsers', 'other'] as const
const B2C_SUCCESS_KEYS = ['roas', 'cpa', 'newCustomers', 'ltv', 'reach', 'otherKpi'] as const
const B2C_CHANNEL_KEYS = ['googleSearch', 'googleDisplay', 'telegramAds', 'metaAds', 'tiktokAds', 'linkedinAds', 'influencer', 'other'] as const

// ─── Helper: resolve option keys to translated strings ──────────────────────

function mapKeys(t: (key: string) => string, prefix: string, keys: readonly string[]): string[] {
    return keys.map(k => t(`${prefix}.${k}`))
}

// ─── Step builders ──────────────────────────────────────────────────────────

function getDetailedSteps(t: (key: string) => string): Step[] {
    return [
        {
            title: t('detailed.page1.title'),
            description: t('detailed.page1.description'),
            fields: [
                { id: 'companyName', label: t('detailed.page1.companyName'), type: 'text', placeholder: t('detailed.page1.companyNamePh'), required: true },
                { id: 'mainActivity', label: t('detailed.page1.mainActivity'), type: 'text', placeholder: t('detailed.page1.mainActivityPh'), required: true },
                { id: 'geographyOfWork', label: t('detailed.page1.geography'), type: 'text', placeholder: t('detailed.page1.geographyPh'), required: true },
                { id: 'companyWebsite', label: t('detailed.page1.website'), type: 'text', placeholder: t('detailed.page1.websitePh') },
                {
                    id: 'social_networks', label: t('detailed.page1.socialNetworks'), type: 'social-inputs',
                    networks: SOCIAL_NETWORK_KEYS.map(k => ({
                        key: k,
                        name: t(`detailed.socialNetworks.${k}`),
                        placeholder: t(`detailed.socialNetworks.${k}Ph`),
                    })),
                },
            ],
        },
        {
            title: t('detailed.page2.title'),
            description: t('detailed.page2.description'),
            fields: [
                { id: 'yourMainGoal', label: t('detailed.page2.mainGoal'), type: 'dropdown', required: true, options: mapKeys(t, 'detailed.mainGoalOptions', MAIN_GOAL_KEYS), values: [...MAIN_GOAL_VALUES] },
                { id: 'clientsAction', label: t('detailed.page2.expectedActions'), type: 'checkbox-group', required: true, options: mapKeys(t, 'detailed.expectedActionsOptions', EXPECTED_ACTION_KEYS) },
                { id: 'campaignDescription', label: t('detailed.page2.campaignDescription'), type: 'textarea', placeholder: t('detailed.page2.campaignDescriptionPh') },
            ],
        },
        {
            title: t('detailed.page3.title'),
            description: t('detailed.page3.description'),
            fields: [
                { id: 'productDescrip', label: t('detailed.page3.productDescription'), type: 'textarea', placeholder: t('detailed.page3.productDescriptionPh'), required: true },
                { id: 'mainAdvantageProduct', label: t('detailed.page3.mainAdvantage'), type: 'textarea', placeholder: t('detailed.page3.mainAdvantagePh'), required: true },
                { id: 'clientPainSolution', label: t('detailed.page3.painPoints'), type: 'textarea', placeholder: t('detailed.page3.painPointsPh'), required: true },
                { id: 'prouctUniqueness', label: t('detailed.page3.differentiators'), type: 'textarea', placeholder: t('detailed.page3.differentiatorsPh'), required: true },
            ],
        },
        {
            title: t('detailed.page4.title'),
            description: t('detailed.page4.description'),
            fields: [
                { id: 'age', label: t('detailed.page4.age'), type: 'text', placeholder: t('detailed.page4.agePh'), width: 'half' },
                { id: 'gender', label: t('detailed.page4.gender'), type: 'dropdown', options: mapKeys(t, 'detailed.genderOptions', GENDER_KEYS), values: [...GENDER_VALUES], width: 'half' },
                { id: 'profession', label: t('detailed.page4.profession'), type: 'text', placeholder: t('detailed.page4.professionPh'), width: 'half' },
                { id: 'incomeLevel', label: t('detailed.page4.incomeLevel'), type: 'dropdown', options: mapKeys(t, 'detailed.incomeLevelOptions', INCOME_KEYS), values: [...INCOME_VALUES], width: 'half' },
                { id: 'interests', label: t('detailed.page4.interests'), type: 'multi-select', options: mapKeys(t, 'detailed.interestOptions', INTEREST_KEYS) },
                { id: 'clientPain', label: t('detailed.page4.painDesires'), type: 'textarea', placeholder: t('detailed.page4.painDesiresPh') },
                { id: 'onlineLoc', label: t('detailed.page4.onlinePresence'), type: 'checkbox-group', options: mapKeys(t, 'detailed.onlineOptions', ONLINE_KEYS), columns: 3 },
                { id: 'offlineLoc', label: t('detailed.page4.offlinePresence'), type: 'checkbox-group', options: mapKeys(t, 'detailed.offlineOptions', OFFLINE_KEYS), columns: 3 },
            ],
        },
        {
            title: t('detailed.page5.title'),
            description: t('detailed.page5.description'),
            fields: [
                { id: 'digitalAdvertising', label: t('detailed.page5.digitalAds'), type: 'checkbox-group', options: mapKeys(t, 'detailed.digitalAdsOptions', DIGITAL_ADS_KEYS) },
                { id: 'outdoorAdvertisement', label: t('detailed.page5.ooh'), type: 'checkbox-group', options: mapKeys(t, 'detailed.oohOptions', OOH_KEYS) },
                { id: 'traditionalMedia', label: t('detailed.page5.traditional'), type: 'checkbox-group', options: mapKeys(t, 'detailed.traditionalOptions', TRADITIONAL_KEYS) },
                { id: 'offlineEvents', label: t('detailed.page5.offlineActivations'), type: 'checkbox-group', options: mapKeys(t, 'detailed.offlineActivationsOptions', OFFLINE_ACT_KEYS) },
            ],
        },
        {
            title: t('detailed.page6.title'),
            description: t('detailed.page6.description'),
            fields: [
                {
                    id: 'budget', label: t('detailed.page6.budget'), type: 'slider',
                    min: 5000, max: 1000000, defaultValue: 5000, unit: '', prefix: '$',
                    formatValue: (val: number) => `$${val.toLocaleString()}`,
                    required: true,
                },
                {
                    id: 'campaignDuration', label: t('detailed.page6.campaignPeriod'), type: 'slider',
                    min: 1, max: 12, defaultValue: 1, unit: t('detailed.page6.months'), prefix: '',
                    formatValue: (val: number) => `${val} ${val > 1 ? t('detailed.page6.months') : t('detailed.page6.month')}`,
                    required: true,
                },
            ],
        },
        {
            title: t('detailed.page7.title'),
            description: t('detailed.page7.description'),
            fields: [
                { id: 'files', label: t('detailed.page7.uploadFiles'), type: 'file-upload', accept: '.pdf,.docx,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4' },
                { id: 'limitations', label: t('detailed.page7.limitations'), type: 'textarea', placeholder: t('detailed.page7.limitationsPh') },
            ],
        },
        {
            title: t('detailed.page8.title'),
            description: t('detailed.page8.description'),
            fields: [
                { id: 'taskOnMinora', label: t('detailed.page8.expectedTasks'), type: 'checkbox-group', required: true, options: mapKeys(t, 'detailed.expectedTasksOptions', EXPECTED_TASKS_KEYS) },
                { id: 'clientWorkPriorities', label: t('detailed.page8.workProcess'), type: 'checkbox-group', required: true, options: mapKeys(t, 'detailed.workProcessOptions', WORK_PROCESS_KEYS) },
            ],
        },
    ]
}

function getShortB2bSteps(t: (key: string) => string): Step[] {
    return [
        {
            title: t('shortB2b.page1.title'),
            description: '',
            fields: [
                { id: 'businessProblems', label: t('shortB2b.page1.question1'), type: 'textarea', placeholder: t('shortB2b.page1.problemPh'), labelStyle: 'h2', hint: t('shortB2b.page1.hint'), required: true },
            ],
        },
        {
            title: t('shortB2b.page2.title'),
            description: '',
            fields: [
                { id: 'productDesciption', label: t('shortB2b.page2.question1'), type: 'text', placeholder: t('shortB2b.page2.productPh'), labelStyle: 'h2', hint: t('shortB2b.page2.hint1'), required: true },
                { id: 'valueProposition', label: t('shortB2b.page2.question2'), type: 'textarea', placeholder: t('shortB2b.page2.uvpPh'), labelStyle: 'h2', hint: t('shortB2b.page2.hint2'), required: true },
            ],
        },
        {
            title: t('shortB2b.page3.title'),
            description: '',
            fields: [
                { id: 'audienceNiche', label: t('shortB2b.page3.question1'), type: 'text', placeholder: t('shortB2b.page3.industryPh'), labelStyle: 'h2', hint: t('shortB2b.page3.hint1'), required: true },
                { id: 'audienceCompanySize', label: t('shortB2b.page3.question2'), type: 'text', placeholder: t('shortB2b.page3.sizePh'), labelStyle: 'h2', hint: t('shortB2b.page3.hint2'), required: true },
                { id: 'audienceYearlyRevenue', label: t('shortB2b.page3.question3'), type: 'text', placeholder: t('shortB2b.page3.revenuePh'), labelStyle: 'h2', hint: t('shortB2b.page3.hint3'), required: true },
                { id: 'audienceLocation', label: t('shortB2b.page3.question4'), type: 'text', placeholder: t('shortB2b.page3.geoPh'), labelStyle: 'h2', hint: t('shortB2b.page3.hint4'), required: true },
                { id: 'audienceUsingSolutions', label: t('shortB2b.page3.question5'), type: 'text', placeholder: t('shortB2b.page3.solutionsPh'), labelStyle: 'h2', hint: t('shortB2b.page3.hint5') },
            ],
        },
        {
            title: t('shortB2b.page4.title'),
            description: '',
            fields: [
                { id: 'decisionMakers', label: t('shortB2b.page4.question1'), type: 'textarea', placeholder: t('shortB2b.page4.dmuPh'), labelStyle: 'h2', hint: t('shortB2b.page4.hint'), required: true },
            ],
        },
        {
            title: t('shortB2b.page5.title'),
            description: '',
            fields: [
                { id: 'webSiteLinkUser', label: t('shortB2b.page5.question1'), type: 'text', placeholder: t('shortB2b.page5.websitePh'), labelStyle: 'h2', hint: t('shortB2b.page5.hint1'), required: true },
                { id: 'companyAssets', label: t('shortB2b.page5.question2'), type: 'file-upload', accept: '.pdf,.docx,.pptx,.jpg,.jpeg,.png,.svg', labelStyle: 'h2', hint: t('shortB2b.page5.hint2') },
                { id: 'restrictions', label: t('shortB2b.page5.question3'), type: 'text', placeholder: t('shortB2b.page5.restrictionsPh'), labelStyle: 'h2', hint: t('shortB2b.page5.hint3'), required: true },
                { id: 'salesObjections', label: t('shortB2b.page5.question4'), type: 'textarea', placeholder: t('shortB2b.page5.objectionsPh'), labelStyle: 'h2', hint: t('shortB2b.page5.hint4'), required: true },
            ],
        },
        {
            title: t('shortB2b.page6.title'),
            description: '',
            fields: [
                { id: 'callToAction', label: t('shortB2b.page6.question1'), type: 'text', placeholder: t('shortB2b.page6.ctaPh'), labelStyle: 'h2', hint: t('shortB2b.page6.hint1'), required: true },
                { id: 'yourOffer', label: t('shortB2b.page6.question2'), type: 'text', placeholder: t('shortB2b.page6.offerPh'), labelStyle: 'h2', hint: t('shortB2b.page6.hint2'), required: true },
                { id: 'mqlDefenition', label: t('shortB2b.page6.question3'), type: 'text', placeholder: t('shortB2b.page6.mqlPh'), labelStyle: 'h2', hint: t('shortB2b.page6.hint3'), required: true },
            ],
        },
        {
            title: t('shortB2b.page7.title'),
            description: '',
            fields: [
                { id: 'budget', label: t('shortB2b.page7.question1'), type: 'text', placeholder: t('shortB2b.page7.budgetPh'), labelStyle: 'h2', required: true },
                { id: 'startDate', label: t('shortB2b.page7.question2'), type: 'date', labelStyle: 'h2', hint: t('shortB2b.page7.hint2') },
                { id: 'duration', label: t('shortB2b.page7.question3'), type: 'text', placeholder: t('shortB2b.page7.cyclePh'), labelStyle: 'h2', hint: t('shortB2b.page7.hint3') },
            ],
        },
        {
            title: t('shortB2b.page8.title'),
            description: '',
            fields: [
                { id: 'successMetric', label: t('shortB2b.page8.question1'), type: 'radio-group', options: mapKeys(t, 'shortB2b.successOptions', B2B_SUCCESS_KEYS), labelStyle: 'h2', hint: t('shortB2b.page8.hint'), required: true },
            ],
        },
    ]
}

function getShortB2cSteps(t: (key: string) => string): Step[] {
    return [
        {
            title: t('shortB2c.page1.title'),
            description: '',
            fields: [
                { id: 'campaignGoal', label: t('shortB2c.page1.question1'), type: 'radio-group', options: mapKeys(t, 'shortB2c.objectiveOptions', B2C_OBJECTIVE_KEYS), labelStyle: 'h2', required: true },
                { id: 'campaignDescription', label: t('shortB2c.page1.question2'), type: 'text', placeholder: t('shortB2c.page1.goalPh'), labelStyle: 'h2', hint: t('shortB2c.page1.hint2'), required: true },
            ],
        },
        {
            title: t('shortB2c.page2.title'),
            description: '',
            fields: [
                { id: 'productDescription', label: t('shortB2c.page2.question1'), type: 'text', placeholder: t('shortB2c.page2.productPh'), labelStyle: 'h2', hint: t('shortB2c.page2.hint1'), required: true },
                { id: 'whatCustomerGets', label: t('shortB2c.page2.question2'), type: 'text', placeholder: t('shortB2c.page2.transformPh'), labelStyle: 'h2', hint: t('shortB2c.page2.hint2'), required: true },
            ],
        },
        {
            title: t('shortB2c.page3.title'),
            description: '',
            fields: [
                { id: 'idealCustomer', label: t('shortB2c.page3.question1'), type: 'text', placeholder: t('shortB2c.page3.customerPh'), labelStyle: 'h2', required: true },
                { id: 'cutomerPain', label: t('shortB2c.page3.question2'), type: 'text', placeholder: t('shortB2c.page3.painPh'), labelStyle: 'h2', hint: t('shortB2c.page3.hint2'), required: true },
                { id: 'customerDream', label: t('shortB2c.page3.question3'), type: 'text', placeholder: t('shortB2c.page3.dreamPh'), labelStyle: 'h2', hint: t('shortB2c.page3.hint3'), required: true },
            ],
        },
        {
            title: t('shortB2c.page4.title'),
            description: '',
            fields: [
                { id: 'whyUpAtNight', label: t('shortB2c.page4.question1'), type: 'text', placeholder: t('shortB2c.page4.enemyPh'), labelStyle: 'h2', hint: t('shortB2c.page4.hint1'), required: true },
                { id: 'painSolution', label: t('shortB2c.page4.question2'), type: 'text', placeholder: t('shortB2c.page4.messagePh'), labelStyle: 'h2', required: true },
            ],
        },
        {
            title: t('shortB2c.page5.title'),
            description: '',
            fields: [
                { id: 'webSiteLinkUser', label: t('shortB2c.page5.question1'), type: 'text', placeholder: t('shortB2c.page5.websitePh'), labelStyle: 'h2', hint: t('shortB2c.page5.hint1'), required: true },
                { id: 'brandAssets', label: t('shortB2c.page5.question2'), type: 'file-upload', accept: '.pdf,.docx,.pptx,.jpg,.jpeg,.png,.svg', labelStyle: 'h2', hint: t('shortB2c.page5.hint2') },
                { id: 'constraints', label: t('shortB2c.page5.question3'), type: 'text', placeholder: t('shortB2c.page5.constraintsPh'), labelStyle: 'h2', hint: t('shortB2c.page5.hint3'), required: true },
            ],
        },
        {
            title: t('shortB2c.page6.title'),
            description: '',
            fields: [
                { id: 'callToAction', label: t('shortB2c.page6.question1'), type: 'text', placeholder: t('shortB2c.page6.ctaPh'), labelStyle: 'h2', hint: t('shortB2c.page6.hint1'), required: true },
                { id: 'riskReducer', label: t('shortB2c.page6.question2'), type: 'text', placeholder: t('shortB2c.page6.offerPh'), labelStyle: 'h2', hint: t('shortB2c.page6.hint2'), required: true },
            ],
        },
        {
            title: t('shortB2c.page7.title'),
            description: '',
            fields: [
                { id: 'budget', label: t('shortB2c.page7.question1'), type: 'text', placeholder: t('shortB2c.page7.budgetPh'), labelStyle: 'h2', hint: t('shortB2c.page7.hint1'), required: true },
                { id: 'startDate', label: t('shortB2c.page7.question2'), type: 'date', labelStyle: 'h2' },
                { id: 'endDate', label: t('shortB2c.page7.question3'), type: 'date', labelStyle: 'h2' },
                { id: 'importantDates', label: t('shortB2c.page7.question4'), type: 'text', placeholder: t('shortB2c.page7.datesPh'), labelStyle: 'h2', hint: t('shortB2c.page7.hint4') },
            ],
        },
        {
            title: t('shortB2c.page8.title'),
            description: t('shortB2c.page8.description'),
            fields: [
                { id: 'promotionChannels', label: t('shortB2c.page8.question1'), type: 'checkbox-group', options: mapKeys(t, 'shortB2c.channelOptions', B2C_CHANNEL_KEYS), labelStyle: 'h2', hint: t('shortB2c.page8.hint1'), columns: 1 },
                { id: 'notUsePromotionChannels', label: t('shortB2c.page8.question2'), type: 'text', placeholder: t('shortB2c.page8.excludePh'), labelStyle: 'h2', hint: t('shortB2c.page8.hint2') },
            ],
        },
        {
            title: t('shortB2c.page9.title'),
            description: '',
            fields: [
                { id: 'successMetrics', label: t('shortB2c.page9.question1'), type: 'radio-group', options: mapKeys(t, 'shortB2c.successOptions', B2C_SUCCESS_KEYS), labelStyle: 'h2', hint: t('shortB2c.page9.hint1'), required: true },
            ],
        },
    ]
}

// ─── Multi-Select Dropdown Component (Tom Select) ──────────────────────────

function MultiSelectDropdown({ field, selected, onChange, t }: {
    field: MultiSelectField
    selected: string[]
    onChange: (values: string[]) => void
    t: (key: string) => string
}) {
    const selectRef = useRef<HTMLSelectElement>(null)
    const tsRef = useRef<any>(null)

    useEffect(() => {
        if (!selectRef.current || tsRef.current) return

        const ts = new TomSelect(selectRef.current, {
            plugins: ['remove_button'],
            maxItems: null,
            hidePlaceholder: true,
            placeholder: t('form.selectInterests'),
            onItemAdd: () => {
                ts.setTextboxValue('')
                ts.refreshOptions()
            },
            onChange: (values: string | string[]) => {
                const arr = Array.isArray(values) ? values : values ? values.split(',') : []
                onChange(arr)
            },
        })

        tsRef.current = ts

        return () => {
            ts.destroy()
            tsRef.current = null
        }
    }, [])

    // Sync external selected values into tom-select
    useEffect(() => {
        const ts = tsRef.current
        if (!ts) return
        const current = ts.getValue() as string[]
        const currentSet = new Set(Array.isArray(current) ? current : current ? [current] : [])
        const selectedSet = new Set(selected)
        if (currentSet.size !== selectedSet.size || ![...currentSet].every(v => selectedSet.has(v))) {
            ts.setValue(selected, true)
        }
    }, [selected])

    return (
        <>
            <style>{`
                .ts-wrapper { background: var(--bg-surface); border: 1px solid var(--border-control); border-radius: 0.75rem; }
                .ts-wrapper.focus { box-shadow: 0 0 0 2px var(--bg-surface), 0 0 0 4px var(--focus-ring); }
                .ts-control { background: var(--bg-surface) !important; border: none !important; color: var(--text-primary); padding: 0.5rem 0.75rem !important; min-height: 38px; }
                .ts-control input { color: var(--text-primary) !important; }
                .ts-control input::placeholder { color: var(--text-tertiary) !important; }
                .ts-control .item { background: var(--bg-sunken) !important; color: var(--text-primary) !important; border: 1px solid var(--border-subtle) !important; border-radius: 9999px !important; padding: 2px 8px !important; font-size: 0.75rem; }
                .ts-control .item .remove { color: var(--text-secondary) !important; border-left: 1px solid var(--border-subtle) !important; }
                .ts-control .item .remove:hover { color: var(--danger) !important; background: transparent !important; }
                .ts-dropdown { background: var(--bg-surface) !important; border: 1px solid var(--border-subtle) !important; color: var(--text-primary); margin-top: 4px; border-radius: 0.75rem !important; box-shadow: var(--shadow-popover); }
                .ts-dropdown .ts-dropdown-content { padding: 4px; }
                .ts-dropdown .option { color: var(--text-secondary); padding: 6px 12px; border-radius: 0.75rem; }
                .ts-dropdown [data-selectable] .highlight { background: var(--brand-200); color: var(--text-primary); }
                .ts-dropdown .option:hover, .ts-dropdown .option.active { background: var(--bg-canvas) !important; color: var(--text-primary); }
                .ts-dropdown .option.selected { display: none; }
            `}</style>
            <select ref={selectRef} multiple defaultValue={selected}>
                {field.options.map(option => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        </>
    )
}

// ─── Slider Input Component ───────────────────────────────────────────────

function SliderInput({ field, value, onValueChange, t }: { field: SliderField, value: number, onValueChange: (val: number) => void, t: any }) {
    const [localValue, setLocalValue] = useState(value.toLocaleString())
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
        if (!isFocused) {
            setLocalValue(value.toLocaleString())
        }
    }, [value, isFocused])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value.replace(/[^0-9]/g, "")
        setLocalValue(input)

        if (input !== "") {
            const num = parseInt(input, 10)
            if (!isNaN(num)) {
                // Clamp on input as requested
                onValueChange(Math.min(Math.max(num, field.min), field.max))
            }
        }
    }

    const handleBlur = () => {
        setIsFocused(false)
        const digits = localValue.replace(/[^0-9]/g, "")
        const num = digits === "" ? field.min : parseInt(digits, 10)
        onValueChange(Math.min(Math.max(isNaN(num) ? field.min : num, field.min), field.max))
    }

    // Determine the unit display (handle singular/plural if it's the duration field)
    const unitDisplay = field.unit 
        ? (field.id === 'campaignDuration' 
            ? (value > 1 ? t('detailed.page6.months') : t('detailed.page6.month'))
            : field.unit)
        : null

    const currentNum = parseInt(localValue.replace(/[^0-9]/g, ""), 10)
    const isOutOfRange = !isNaN(currentNum) && (currentNum < field.min || currentNum > field.max)

    return (
        <div className="flex flex-col items-center">
            <div className="flex items-center justify-center text-text-primary font-bold group">
                {field.prefix && <span className="text-2xl mr-1 select-none">{field.prefix}</span>}
                <input
                    type="text"
                    value={isFocused ? localValue : value.toLocaleString()}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    className={cn(
                        "bg-transparent border-none text-center text-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-lg p-0 cursor-text min-w-[20px] transition-colors",
                        isOutOfRange ? "text-danger" : "text-text-primary"
                    )}
                    style={{ width: `${Math.max(isFocused ? localValue.length : value.toLocaleString().length, 1)}ch` }}
                />
                {unitDisplay && (
                    <span className={cn(
                        "text-lg ml-2 lowercase font-medium select-none transition-colors",
                        isOutOfRange ? "text-danger" : "text-text-secondary"
                    )}>
                        {unitDisplay}
                    </span>
                )}
            </div>
            {isOutOfRange && (
                <span className="text-2xs text-danger absolute -bottom-4 whitespace-nowrap animate-in fade-in slide-in-from-top-1">
                    {field.id === 'budget' 
                        ? `Budget: $${field.min.toLocaleString()} — $${field.max.toLocaleString()}`
                        : `Range: ${field.min} — ${field.max}`}
                </span>
            )}
        </div>
    )
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function BriefFormPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const t = useTranslations('brief')
    const { i18n } = useTranslation()

    const projectName = searchParams.get('name') || 'Untitled Project'
    const briefType = searchParams.get('type') || 'short'
    const businessType = searchParams.get('business') || 'b2b'
    const chatId = searchParams.get('chatId') || ''
    const { user } = useAuth()
    const { activeTeam } = useActiveTeam()

    const steps = useMemo(() => {
        if (briefType === 'detailed') return getDetailedSteps(t)
        if (businessType === 'b2b') return getShortB2bSteps(t)
        return getShortB2cSteps(t)
    }, [briefType, businessType, t])

    interface AudiencePair {
        id: number
        idealCustomer: string
        cutomerPain: string
    }

    interface DreamItem {
        id: number
        value: string
    }

    const [currentStep, setCurrentStep] = useState(0)
    const [answers, setAnswers] = useState<Record<string, any>>({})
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
    const [errorFields, setErrorFields] = useState<Set<string>>(new Set())
    const [isSubmitting, setIsSubmitting] = useState(false)

    const step = steps[currentStep]
    const isFirstStep = currentStep === 0
    const isLastStep = currentStep === steps.length - 1
    const progress = ((currentStep + 1) / steps.length) * 100

    useEffect(() => {
        if (briefType === 'short' && businessType === 'b2c') {
            setAnswers(prev => ({
                ...prev,
                audience_pairs: prev.audience_pairs || [
                    { id: 1, idealCustomer: '', cutomerPain: '' }
                ],
                customerDream: prev.customerDream || [
                    { id: 1, value: '' }
                ]
            }))
        }
    }, [briefType, businessType])

    const handleUpdatePair = (index: number, key: 'idealCustomer' | 'cutomerPain', value: string) => {
        const pairs = [...((answers.audience_pairs as AudiencePair[]) || [
            { id: 1, idealCustomer: '', cutomerPain: '' }
        ])]
        pairs[index] = { ...pairs[index], [key]: value }
        
        setAnswers(prev => ({
            ...prev,
            audience_pairs: pairs
        }))

        setErrorFields(prev => {
            const next = new Set(prev)
            next.delete(`${key}-${index}`)
            return next
        })
    }

    const handleAddPair = () => {
        const pairs = [...((answers.audience_pairs as AudiencePair[]) || [
            { id: 1, idealCustomer: '', cutomerPain: '' }
        ])]
        if (pairs.length >= 6) return
        const nextId = Math.max(...pairs.map(p => p.id), 0) + 1
        pairs.push({ id: nextId, idealCustomer: '', cutomerPain: '' })
        setAnswers(prev => ({
            ...prev,
            audience_pairs: pairs
        }))
    }

    const handleRemovePair = (index: number) => {
        const pairs = [...((answers.audience_pairs as AudiencePair[]) || [
            { id: 1, idealCustomer: '', cutomerPain: '' }
        ])]
        if (pairs.length > 1 && index > 0 && index < pairs.length) {
            pairs.splice(index, 1)
            setAnswers(prev => ({
                ...prev,
                audience_pairs: pairs
            }))
        }
    }

    const handleUpdateDream = (index: number, value: string) => {
        const dreams = [...((answers.customerDream as DreamItem[]) || [
            { id: 1, value: '' }
        ])]
        dreams[index] = { ...dreams[index], value }
        
        setAnswers(prev => ({
            ...prev,
            customerDream: dreams
        }))

        setErrorFields(prev => {
            const next = new Set(prev)
            next.delete(`customerDream-${index}`)
            return next
        })
    }

    const handleAddDream = () => {
        const dreams = [...((answers.customerDream as DreamItem[]) || [
            { id: 1, value: '' }
        ])]
        if (dreams.length >= 6) return
        const nextId = Math.max(...dreams.map(d => d.id), 0) + 1
        dreams.push({ id: nextId, value: '' })
        setAnswers(prev => ({
            ...prev,
            customerDream: dreams
        }))
    }

    const handleRemoveDream = (index: number) => {
        const dreams = [...((answers.customerDream as DreamItem[]) || [
            { id: 1, value: '' }
        ])]
        if (dreams.length > 1 && index > 0 && index < dreams.length) {
            dreams.splice(index, 1)
            setAnswers(prev => ({
                ...prev,
                customerDream: dreams
            }))
        }
    }

    const handleAnswer = (fieldId: string, value: string | string[]) => {
        setAnswers(prev => ({ ...prev, [fieldId]: value }))
        setErrorFields(prev => { const next = new Set(prev); next.delete(fieldId); return next })
    }

    const handleCheckboxToggle = (fieldId: string, option: string) => {
        const current = (answers[fieldId] as string[] | undefined) || []
        const updated = current.includes(option)
            ? current.filter(v => v !== option)
            : [...current, option]
        handleAnswer(fieldId, updated)
    }

    const handleSliderChange = (fieldId: string, value: number) => {
        handleAnswer(fieldId, String(value))
    }

    const handleNext = () => {
        if (briefType === 'short' && businessType === 'b2c' && currentStep === 2) {
            const pairs = (answers.audience_pairs as AudiencePair[]) || [
                { id: 1, idealCustomer: '', cutomerPain: '' }
            ]
            const dreams = (answers.customerDream as DreamItem[]) || [
                { id: 1, value: '' }
            ]

            const nextErrorFields = new Set<string>()
            let hasError = false

            pairs.forEach((pair, index) => {
                if (!pair.idealCustomer || pair.idealCustomer.trim() === '') {
                    nextErrorFields.add(`idealCustomer-${index}`)
                    hasError = true
                }
                if (!pair.cutomerPain || pair.cutomerPain.trim() === '') {
                    nextErrorFields.add(`cutomerPain-${index}`)
                    hasError = true
                }
            })

            dreams.forEach((dream, index) => {
                if (!dream.value || dream.value.trim() === '') {
                    nextErrorFields.add(`customerDream-${index}`)
                    hasError = true
                }
            })

            if (hasError) {
                setErrorFields(nextErrorFields)
                return
            }
            setErrorFields(new Set())
        } else {
            // Validate required fields
            const requiredFields = step.fields.filter(f => 'required' in f && f.required)
            const emptyIds = requiredFields
                .filter(f => {
                    const val = answers[f.id]
                    if (Array.isArray(val)) return val.length === 0
                    return !val || (typeof val === 'string' && val.trim() === '')
                })
                .map(f => f.id)

            if (emptyIds.length > 0) {
                setErrorFields(new Set(emptyIds))
                return
            }
            setErrorFields(new Set())
        }

        if (isLastStep) {
            handleSubmitBrief()
        } else {
            setCurrentStep(prev => prev + 1)
        }
    }

    const prepareBriefForSubmit = (): Record<string, unknown> => {
        const result: Record<string, unknown> = { ...answers }
        if (briefType === 'detailed') {
            if (typeof result.budget === 'string' && result.budget !== '') {
                result.budget = parseInt(result.budget, 10)
            }
            if (typeof result.campaignDuration === 'string' && result.campaignDuration !== '') {
                result.campaignDuration = parseInt(result.campaignDuration, 10)
            }
        }
        return result
    }

    const handleSubmitBrief = async () => {
        if (!user?.id) {
            toast.error('Missing user info')
            return
        }

        setIsSubmitting(true)
        try {
            const response = await api.submitBrief({
                userId: user.id,
                ...(activeTeam ? { teamId: activeTeam.id } : {}),
                briefType: briefType as 'short' | 'detailed',
                businessType: businessType as 'b2b' | 'b2c',
                brief: prepareBriefForSubmit(),
                language: (i18n.resolvedLanguage ?? i18n.language ?? 'ru') as 'en' | 'ru',
                ...(chatId ? { chatId } : {}),  // only pass chatId if it exists
            })

            const resolvedChatId = response?.chatId ?? chatId
            console.log('[BriefFormPage] Brief submitted successfully')
            toast.success('Brief submitted successfully!')
            if (resolvedChatId) {
                navigate(`/workspace/${resolvedChatId}`)
            } else {
                navigate('/projects')
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to submit brief'
            console.error('[BriefFormPage] Error submitting brief:', message)
            toast.error(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    // ─── Field Renderers ────────────────────────────────────────────────────

    const renderField = (field: BriefField) => {
        switch (field.type) {
            case 'text':
                return (
                    <Textarea
                        value={(answers[field.id] as string) || ''}
                        onChange={(e) => handleAnswer(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        rows={1}
                        onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                        className={cn(
                            'resize-none overflow-hidden',
                            errorFields.has(field.id) && fieldErrorClassName
                        )}
                    />
                )

            case 'textarea':
                return (
                    <Textarea
                        value={(answers[field.id] as string) || ''}
                        onChange={(e) => handleAnswer(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        rows={3}
                        onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                        className={cn(
                            "resize-none overflow-hidden",
                            errorFields.has(field.id) && fieldErrorClassName
                        )}
                    />
                )

            case 'dropdown':
                return (
                    <Select
                        value={(answers[field.id] as string) || ''}
                        onChange={(e) => handleAnswer(field.id, e.target.value)}
                        className={cn(errorFields.has(field.id) && fieldErrorClassName)}
                    >
                        <option value="" disabled>{t('form.select')}</option>
                        {field.options.map((opt, i) => (
                            <option key={opt} value={field.values?.[i] ?? opt}>{opt}</option>
                        ))}
                    </Select>
                )

            case 'checkbox-group': {
                const cols = field.columns || 2
                const selectedValues = (answers[field.id] as string[]) || []
                const isPromotionField = field.id === 'promotionChannels'
                const promotionOtherLabel = isPromotionField ? t('shortB2c.channelOptions.other') : null
                const isPromotionOtherSelected =
                    isPromotionField && promotionOtherLabel
                        ? selectedValues.includes(promotionOtherLabel)
                        : false

                return (
                    <div className="space-y-3">
                        <div className={cn("grid gap-3", cols === 1 ? "grid-cols-1" : cols === 3 ? "grid-cols-3" : "grid-cols-2")}>
                            {field.options.map(option => {
                                const checked = selectedValues.includes(option)
                                return (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => handleCheckboxToggle(field.id, option)}
                                        className="flex items-center gap-2 text-sm text-left transition-all"
                                    >
                                        <CheckboxMark checked={checked} />
                                        <span className={cn(
                                            "truncate",
                                            checked ? "text-text-primary" : "text-text-secondary"
                                        )}>{option}</span>
                                    </button>
                                )
                            })}
                        </div>
                        {isPromotionOtherSelected && (
                            <Textarea
                                value={(answers[`${field.id}_other`] as string) || ''}
                                onChange={(e) => handleAnswer(`${field.id}_other`, e.target.value)}
                                placeholder={t('form.myAnswer')}
                                rows={1}
                                onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                                className="resize-none overflow-hidden"
                            />
                        )}
                    </div>
                )
            }

            case 'multi-select':
                return (
                    <MultiSelectDropdown
                        field={field}
                        selected={(answers[field.id] as string[]) || []}
                        onChange={(values) => handleAnswer(field.id, values)}
                        t={t}
                    />
                )

            case 'slider': {
                const val = Number(answers[field.id]) || field.defaultValue
                return (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 items-center">
                            <span className="text-sm text-text-tertiary text-left">
                                {field.formatValue?.(field.min) ?? `${field.prefix}${field.min}`}
                            </span>
                            <div className="flex justify-center">
                                <SliderInput
                                    field={field}
                                    value={val}
                                    onValueChange={(newVal) => handleSliderChange(field.id, newVal)}
                                    t={t}
                                />
                            </div>
                            <span className="text-sm text-text-tertiary text-right">
                                {field.formatValue?.(field.max) ?? `${field.prefix}${field.max}`}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={field.min}
                            max={field.max}
                            value={val}
                            onChange={(e) => handleSliderChange(field.id, Number(e.target.value))}
                            className="w-full accent-brand-400 h-2 rounded-full cursor-pointer"
                        />
                    </div>
                )
            }

            case 'file-upload':
                return (
                    <div>
                        <p className="text-sm text-text-tertiary italic mb-2">{t('form.uploadLimit')}</p>
                        <label className="flex flex-col items-center justify-center border-2 border-dashed border-border-control rounded-xl p-6 cursor-pointer hover:border-brand-700 hover:bg-brand-50 transition-colors">
                            <Upload size={28} className="text-text-tertiary mb-2" />
                            <span className="text-sm text-text-tertiary">{t('form.uploadHint')}</span>
                            <span className="text-xs text-text-tertiary mt-1">{t('form.uploadTypes')}</span>
                            <input
                                type="file"
                                multiple
                                accept={field.accept}
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files) {
                                        setUploadedFiles(prev => [...prev, ...Array.from(e.target.files!)])
                                    }
                                }}
                            />
                        </label>
                        {uploadedFiles.length > 0 && (
                            <div className="mt-3 space-y-1.5">
                                {uploadedFiles.map((file, i) => (
                                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-bg-sunken text-sm">
                                        <span className="text-text-primary truncate">{file.name}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-xs"
                                            onClick={() => setUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
                                            className="text-text-tertiary hover:text-danger flex-shrink-0 ml-2"
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )

            case 'social-inputs':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {field.networks.map(network => (
                            <div key={network.name}>
                                <label className="block text-xs font-medium text-text-tertiary mb-1">{network.name}</label>
                                <Input
                                    value={(answers[network.key] as string) || ''}
                                    onChange={(e) => handleAnswer(network.key, e.target.value)}
                                    placeholder={network.placeholder}
                                />
                            </div>
                        ))}
                    </div>
                )

            case 'radio-group':
                const lastOption = field.options[field.options.length - 1]
                const isOtherSelected = (answers[field.id] as string) === lastOption
                return (
                    <div className="space-y-3">
                        {field.options.map(option => {
                            const selected = (answers[field.id] as string) === option
                            return (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => handleAnswer(field.id, option)}
                                    className="flex items-center gap-3 w-full text-sm text-left transition-all"
                                >
                                    <div className={cn(
                                        "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                        selected ? "border-brand-700 bg-brand-400" : "border-border-control bg-bg-surface"
                                    )}>
                                        {selected && <div className="w-1.5 h-1.5 rounded-full bg-text-on-brand" />}
                                    </div>
                                    <span className={cn(
                                        selected ? "text-text-primary" : "text-text-secondary"
                                    )}>{option}</span>
                                </button>
                            )
                        })}
                        {isOtherSelected && (
                            <Textarea
                                value={(answers[`${field.id}_other`] as string) || ''}
                                onChange={(e) => handleAnswer(`${field.id}_other`, e.target.value)}
                                placeholder={t('form.myAnswer')}
                                rows={1}
                                onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                                className="resize-none overflow-hidden ml-7"
                            />
                        )}
                    </div>
                )

            case 'date':
                return (
                    <Input
                        type="date"
                        value={(answers[field.id] as string) || ''}
                        onChange={(e) => handleAnswer(field.id, e.target.value)}
                        className={cn(errorFields.has(field.id) && fieldErrorClassName)}
                    />
                )

            default:
                return null
        }
    }

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
            >
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/projects/new?name=${encodeURIComponent(projectName)}`)}
                    className="mb-3 -ml-2"
                >
                    <ArrowLeft size={16} />
                    {t('form.backToSetup')}
                </Button>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-text-primary">{projectName}</h1>
                        <p className="text-sm text-text-tertiary mt-0.5">
                            {briefType === 'detailed' ? t('form.detailedBrief') : t('form.shortBrief')} · {businessType.toUpperCase()} · {t('form.step')} {currentStep + 1} {t('form.of')} {steps.length}
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4 h-1.5 rounded-full bg-bg-sunken overflow-hidden">
                    <motion.div
                        className="h-full rounded-full bg-brand-400"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </motion.div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                >
                    <Card className="bg-bg-canvas">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-bold text-text-primary">
                                {step.title}
                            </h2>
                            {step.description && (
                                <p className="text-sm text-text-tertiary mt-1 mb-6">
                                    {step.description}
                                </p>
                            )}
                            {!step.description && <div className="mb-6" />}

                            <div className="space-y-5">
                                {briefType === 'short' && businessType === 'b2c' && currentStep === 2 ? (
                                    <div className="space-y-6">
                                        {/* 1. Who do you consider your ideal customer? */}
                                        {(() => {
                                            const customerField = step.fields[0] as any
                                            const pairs = (answers.audience_pairs as AudiencePair[]) || [
                                                { id: 1, idealCustomer: '', cutomerPain: '' }
                                            ]
                                            return (
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <label className="block text-sm font-medium text-text-primary mb-1">
                                                            {customerField.label} <span className="text-danger">*</span>
                                                        </label>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {pairs.map((pair, index) => (
                                                            <div key={pair.id} className="flex gap-3 items-start">
                                                                <div className="flex-1">
                                                                    <Textarea
                                                                        value={pair.idealCustomer}
                                                                        onChange={(e) => handleUpdatePair(index, 'idealCustomer', e.target.value)}
                                                                        placeholder={customerField.placeholder}
                                                                        rows={1}
                                                                        onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                                                                        className={cn(
                                                                            'resize-none overflow-hidden',
                                                                            errorFields.has(`idealCustomer-${index}`) && fieldErrorClassName
                                                                        )}
                                                                    />
                                                                </div>
                                                                {index === 0 ? (
                                                                    pairs.length < 6 && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="secondary"
                                                                            size="icon-lg"
                                                                            onClick={handleAddPair}
                                                                            className="flex-shrink-0"
                                                                        >
                                                                            <Plus size={18} />
                                                                        </Button>
                                                                    )
                                                                ) : (
                                                                    <Button
                                                                        type="button"
                                                                        variant="secondary"
                                                                        size="icon-lg"
                                                                        onClick={() => handleRemovePair(index)}
                                                                        className="flex-shrink-0"
                                                                    >
                                                                        <Minus size={18} />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        })()}

                                        {/* 2. What keeps them up at night? */}
                                        {(() => {
                                            const painField = step.fields[1] as any
                                            const pairs = (answers.audience_pairs as AudiencePair[]) || [
                                                { id: 1, idealCustomer: '', cutomerPain: '' }
                                            ]
                                            return (
                                                <div className="space-y-3 pt-4 border-t border-border-subtle">
                                                    <label className="block text-sm font-medium text-text-primary mb-1">
                                                        {painField.label} <span className="text-danger">*</span>
                                                    </label>
                                                    {painField.hint && (
                                                        <div className="text-xs text-text-tertiary italic whitespace-pre-line leading-relaxed mb-2">
                                                            {painField.hint}
                                                        </div>
                                                    )}
                                                    <div className="space-y-3">
                                                        {pairs.map((pair, index) => (
                                                            <div key={pair.id} className="flex gap-3 items-start">
                                                                <div className="flex-1">
                                                                    <Textarea
                                                                        value={pair.cutomerPain}
                                                                        onChange={(e) => handleUpdatePair(index, 'cutomerPain', e.target.value)}
                                                                        placeholder={painField.placeholder}
                                                                        rows={1}
                                                                        onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                                                                        className={cn(
                                                                            'resize-none overflow-hidden',
                                                                            errorFields.has(`cutomerPain-${index}`) && fieldErrorClassName
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        })()}

                                        {/* 3. What do they secretly dream of? */}
                                        {(() => {
                                            const dreamField = step.fields[2] as any
                                            const dreams = (answers.customerDream as DreamItem[]) || [
                                                { id: 1, value: '' }
                                            ]
                                            return (
                                                <div className="space-y-3 pt-4 border-t border-border-subtle">
                                                    <div className="flex justify-between items-center">
                                                        <label className="block text-sm font-medium text-text-primary mb-1">
                                                            {dreamField.label} <span className="text-danger">*</span>
                                                        </label>
                                                    </div>
                                                    {dreamField.hint && (
                                                        <div className="text-xs text-text-tertiary italic whitespace-pre-line leading-relaxed mb-2">
                                                            {dreamField.hint}
                                                        </div>
                                                    )}
                                                    <div className="space-y-3">
                                                        {dreams.map((dream, index) => (
                                                            <div key={dream.id} className="flex gap-3 items-start">
                                                                <div className="flex-1">
                                                                    <Textarea
                                                                        value={dream.value}
                                                                        onChange={(e) => handleUpdateDream(index, e.target.value)}
                                                                        placeholder={dreamField.placeholder}
                                                                        rows={1}
                                                                        onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' }}
                                                                        className={cn(
                                                                            'resize-none overflow-hidden',
                                                                            errorFields.has(`customerDream-${index}`) && fieldErrorClassName
                                                                        )}
                                                                    />
                                                                </div>
                                                                {index === 0 ? (
                                                                    dreams.length < 6 && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="secondary"
                                                                            size="icon-lg"
                                                                            onClick={handleAddDream}
                                                                            className="flex-shrink-0"
                                                                        >
                                                                            <Plus size={18} />
                                                                        </Button>
                                                                    )
                                                                ) : (
                                                                    <Button
                                                                        type="button"
                                                                        variant="secondary"
                                                                        size="icon-lg"
                                                                        onClick={() => handleRemoveDream(index)}
                                                                        className="flex-shrink-0"
                                                                    >
                                                                        <Minus size={18} />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        })()}
                                    </div>
                                ) : (
                                    (() => {
                                        const elements: React.ReactNode[] = []
                                        let i = 0
                                        while (i < step.fields.length) {
                                            const field = step.fields[i]
                                            const hasWidth = 'width' in field && field.width === 'half'
                                            const nextField = step.fields[i + 1]
                                            const nextHasWidth = nextField && 'width' in nextField && nextField.width === 'half'

                                            if (hasWidth && nextHasWidth) {
                                                elements.push(
                                                    <div key={`row-${field.id}`} className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-text-primary mb-1.5">{field.label}</label>
                                                            {renderField(field)}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-text-primary mb-1.5">{nextField.label}</label>
                                                            {renderField(nextField)}
                                                        </div>
                                                    </div>
                                                )
                                                i += 2
                                            } else {
                                                const isH2Label = 'labelStyle' in field && field.labelStyle === 'h2'
                                                const fieldHint = 'hint' in field ? (field as any).hint : undefined
                                                const isRequired = 'required' in field && field.required
                                                elements.push(
                                                    <div key={field.id}>
                                                        {isH2Label ? (
                                                            <h3 className="text-base font-bold text-text-primary mb-2">
                                                                {field.label} {isRequired && <span className="text-danger">*</span>}
                                                            </h3>
                                                        ) : (
                                                            <label className="block text-sm font-medium text-text-primary mb-1.5">
                                                                {field.label} {isRequired && <span className="text-danger">*</span>}
                                                            </label>
                                                        )}
                                                        {fieldHint && (
                                                            <div className="text-sm text-text-tertiary italic mb-3 whitespace-pre-line leading-relaxed">
                                                                {(fieldHint as string).split('\n').map((line: string, li: number) => {
                                                                    const parts = line.split(/\*\*(.*?)\*\*/g)
                                                                    return (
                                                                        <span key={li}>
                                                                            {parts.map((part: string, pi: number) =>
                                                                                pi % 2 === 1
                                                                                    ? <strong key={pi} className="font-bold not-italic text-text-primary">{part}</strong>
                                                                                    : <span key={pi}>{part}</span>
                                                                            )}
                                                                            {li < (fieldHint as string).split('\n').length - 1 && '\n'}
                                                                        </span>
                                                                    )
                                                                })}
                                                            </div>
                                                        )}
                                                        {renderField(field)}
                                                    </div>
                                                )
                                                i += 1
                                            }
                                        }
                                        return elements
                                    })()
                                )}
                            </div>

                            {/* Navigation Buttons */}
                            <div className="flex justify-between  items-center pt-6 mt-6">
                                <Button
                                    variant="secondary"
                                    onClick={() => setCurrentStep(prev => prev - 1)}
                                    disabled={isFirstStep}
                                >
                                    <ArrowLeft size={16} />
                                    {t('form.back')}
                                </Button>

                                <Button onClick={handleNext} disabled={isSubmitting}>
                                    {isLastStep ? (
                                        <>
                                            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                            {briefType === 'detailed' ? t('form.launchCampaign') : t('form.completeBrief')}
                                        </>
                                    ) : (
                                        <>
                                            {t('form.next')}
                                            <ArrowRight size={16} />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </AnimatePresence>
        </div>
    )
}
